import React from 'react';
import { Line } from 'react-chartjs-2';
import { TaskCompletionData } from '../types';
import styles from '../../../../../../styles/OverviewTab.module.css';

interface TaskCompletionChartProps {
  data: TaskCompletionData[];
}

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    intersect: false,
    mode: 'index' as const,
  },
  plugins: {
    legend: {
      display: true,
      position: 'top' as const,
      labels: {
        usePointStyle: true,
        padding: 20,
        font: {
          size: 12,
          weight: 'normal' as const,
          family: 'var(--font-main)',
        },
        color: '#374151',
      },
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      padding: 12,
      titleFont: {
        size: 13,
        weight: 'bold' as const,
        family: 'var(--font-main)',
      },
      bodyFont: {
        size: 12,
        weight: 'normal' as const,
        family: 'var(--font-main)',
      },
      titleColor: '#ffffff',
      bodyColor: '#ffffff',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      cornerRadius: 8,
      displayColors: true,
      callbacks: {
        title: (context: any) => {
          return `Date: ${context[0].label}`;
        },
        label: (context: any) => {
          return `${context.dataset.label}: ${context.parsed.y}`;
        },
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
        drawBorder: false,
      },
      ticks: {
        font: {
          size: 11,
          family: 'var(--font-main)',
        },
        color: '#6b7280',
        maxRotation: 45,
        minRotation: 0,
      },
      border: {
        display: true,
        color: '#e5e7eb',
      },
    },
    y: {
      beginAtZero: true,
      grid: {
        color: '#f3f4f6',
        drawBorder: false,
        lineWidth: 1,
      },
      ticks: {
        font: {
          size: 11,
          family: 'var(--font-main)',
        },
        color: '#6b7280',
        stepSize: 1,
      },
      border: {
        display: true,
        color: '#e5e7eb',
      },
    },
  },
  elements: {
    point: {
      radius: 4,
      hoverRadius: 6,
      borderWidth: 2,
      hoverBorderWidth: 3,
    },
    line: {
      borderWidth: 2.5,
      tension: 0.4,
    },
  },
};

const TaskCompletionChart: React.FC<TaskCompletionChartProps> = ({ data }) => {
  const chartData = data.slice(-14); // Last 14 days
  
  const chartConfig = {
    labels: chartData.map(d => new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
    datasets: [
      {
        label: 'Tasks Completed',
        data: chartData.map(d => d.completed),
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        pointBackgroundColor: '#3b82f6',
        pointBorderColor: '#ffffff',
        pointHoverBackgroundColor: '#ffffff',
        pointHoverBorderColor: '#3b82f6',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  return (
    <div className={styles.overviewCard}>
      <h3 className={styles.cardTitle}>
        <svg className={styles.cardIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 20V10"></path>
          <path d="M12 20V4"></path>
          <path d="M6 20v-6"></path>
        </svg>
        Task Completion Trend
      </h3>
      <div className={styles.chartContainer}>
        {data && data.length > 0 ? (
          <Line data={chartConfig} options={chartOptions} />
        ) : (
          <div className={styles.chartPlaceholder}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 20V10"></path>
              <path d="M12 20V4"></path>
              <path d="M6 20v-6"></path>
            </svg>
            <span>No data available</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskCompletionChart;

